// The V6 charge nurse incident inbox (console Worklist, phone To do). Served by
// src/aithora/v3/oversight/incident-reports.ts. An incident report is a Guardian signal
// with a draft on it: its steps are Guardian steps (GuardianStepRow), each sealed to the
// ledger, and it shows on GET /guardian/signals with `incidentReport` set.
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';
import type { GuardianDeadline, GuardianKind, GuardianStepRow, GuardianTold } from './guardian-routes';

const A = AITHORA_PREFIX;

/** INCIDENT_TYPES in incident-reports.ts (point-of-care.js incTypes, plus Elopement). */
export type IncidentType =
  | 'Fall'
  | 'Injury of unknown source'
  | 'Abuse or neglect allegation'
  | 'Skin tear or injury'
  | 'Choking'
  | 'Behavior'
  | 'Medication'
  | 'Wandering'
  | 'Elopement'
  | 'Other';

export type IncidentInjury = 'yes' | 'no' | 'not_sure';

/** Who is reading: the charge nurse on shift and the Facility Admin act; others read. */
export type IncidentViewer = 'charge_nurse' | 'facility_admin' | 'care_staff' | 'aithora';

/** What still has to happen before Sign. */
export type IncidentSignBlocker = 'doctor_told' | 'family_told' | 'state_report_filed';

/** How the family was told: a message in the app, or the nurse called them. */
export type FamilyToldMethod = 'in_app' | 'phone';
/** How the doctor was told. */
export type DoctorToldMethod = 'phone' | 'fax' | 'portal';
export type IncidentToldMethod = FamilyToldMethod | DoctorToldMethod;

/** How a Tell the doctor or Tell the family stamp was done (null on a stamp made before this was recorded). */
export interface IncidentToldHow {
  method: IncidentToldMethod;
  /** Who the nurse spoke with (or faxed, or sent to through the portal). Null for a message in the app. */
  spokenWith: string | null;
  /** The family message sent in the app (family_messages id). */
  messageId: string | null;
  /** The family members on the 483.10(g)(14) notification record for this incident. */
  notified: { userId: string; name: string | null }[];
  /** The resident's practitioner the doctor step was about, when one was picked. */
  practitionerId: string | null;
  /**
   * Tell the family in the app: how each linked family member was alerted outside the app on
   * the channels they chose ("Text to Ann · sent"), and `callNow` in plain words when someone
   * was not reached. Null on every other stamp (absent on a stamp from before alerts existed).
   */
  alerts?: IncidentFamilyAlerts | null;
}

/** The family alerts an in-app Tell the family sent (v3/family/family-alerts.ts). */
export interface IncidentFamilyAlerts {
  /** One line per attempt: "Text to Ann · sent", "Ben has not chosen how to get alerts". */
  lines: string[];
  /** Everyone was reached outside the app on at least one channel. */
  reachedAll: boolean;
  /** "Ben was not alerted outside the app. Call them." when someone was not reached; else null. */
  callNow: string | null;
}

/** One stamp on a report: who did it, when, and its ledger reference. */
export interface IncidentStamp {
  at: string;
  by: { userId: string | null; name: string | null; role: string | null };
  /** Tell the doctor: what the doctor said or ordered. Tell the family by phone: the nurse's note. */
  note: string | null;
  ledgerRef: string | null;
  /** Doctor told and family told only: how. Absent on other stamps. */
  how?: IncidentToldHow | null;
}

/**
 * The words after a stamp's time on the chip: "in the app", "by phone, spoke with Ann Lee",
 * "by fax to Dr. Lee", "through the portal to Dr. Lee". Empty when the stamp says nothing of how.
 */
export function incidentToldHowText(how: IncidentToldHow | null | undefined): string {
  if (!how) return '';
  const who = how.spokenWith ? how.spokenWith.trim() : '';
  switch (how.method) {
    case 'in_app': return how.alerts?.lines.length ? `in the app · ${how.alerts.lines.join(' · ')}` : 'in the app';
    case 'phone': return who ? `by phone, spoke with ${who}` : 'by phone';
    case 'fax': return who ? `by fax to ${who}` : 'by fax';
    case 'portal': return who ? `through the portal to ${who}` : 'through the portal';
    default: return '';
  }
}

export interface IncidentReport {
  /** The Guardian signal id. */
  id: string;
  caseId: string;
  organizationId: string;
  residentId: string | null;
  residentName: string | null;
  room: string | null;
  type: IncidentType;
  /** The Guardian kind it is listed as (fall, abuse_neglect, other...). */
  kind: GuardianKind;
  witnessed: boolean | null;
  injury: IncidentInjury | null;
  seriousInjury: boolean | null;
  /** In the reporter's own words. */
  whatHappened: string | null;
  immediateSteps: { label: string; done: boolean }[];
  reportedBy: { userId: string | null; name: string | null };
  reportedAt: string;
  /** Always 'draft' until the charge nurse or Facility Admin signs. */
  status: 'draft' | 'signed';
  stage: string;
  stateReport: {
    required: boolean;
    /** serious_bodily_injury = 2 hours, standard = 24 hours, null = no state report. */
    tier: 'serious_bodily_injury' | 'standard' | null;
    /** The Guardian clock (null deadlineAt when none can be stated, with the reason). */
    deadline: GuardianDeadline;
    overdue: boolean;
    filed: IncidentStamp | null;
  };
  /** 5 working days after the report, when a state report is needed. */
  investigationDueAt: string | null;
  doctorTold: IncidentStamp | null;
  familyTold: IncidentStamp | null;
  signed: { at: string | null; by: { userId: string | null; name: string | null }; ledgerRef: string | null } | null;
  signBlockers: IncidentSignBlocker[];
  /** The blockers in plain words ("Tell the doctor and tell the family before you sign."). */
  signWhy: string | null;
  escalation: {
    /** Unsigned at this time, the DON is told. */
    toDonAt: string;
    /** Unsigned at this time, the administrator is told. */
    toAdministratorAt: string;
    sentToDon: IncidentStamp | null;
    sentToAdministrator: IncidentStamp | null;
  };
  /** A fall's post-fall checks on the Worklist / To do: one per shift for 72 hours. */
  postFallChecks: { taskId: string; startsAt: string }[];
  /** No photo upload exists yet: always null. */
  photo: null;
  steps: GuardianStepRow[];
}

export interface IncidentScope {
  organizationId: string;
  viewer: IncidentViewer;
  canAct: boolean;
}

export interface IncidentTypesResponse {
  real: true;
  types: {
    type: IncidentType;
    immediateSteps: string[];
    postFallChecks: boolean;
    stateReport: 'two_hours' | 'twenty_four_hours' | 'only_if_serious_injury';
  }[];
  rules: {
    stateReportNote: string;
    unsignedToDonMinutes: number;
    unsignedToAdministratorMinutes: number;
    investigationWorkingDays: number;
    postFallHours: number;
    postFallShiftHours: number;
    photo: string;
  };
}

/** GET /guardian/incident-types: the types, each type's immediate steps and its clock. */
export const incidentTypes = route<IncidentTypesResponse>()('GET', `${A}/guardian/incident-types`, { honest: true });

export interface FileIncidentBody {
  type: IncidentType;
  residentId: string;
  witnessed?: boolean | null;
  injury?: IncidentInjury | null;
  /** A serious injury puts the report on the 2-hour clock. */
  seriousInjury?: boolean | null;
  whatHappened: string;
  /** The immediate steps taken, from that type's list. */
  immediateSteps?: string[];
}

/** POST /guardian/incident-reports: Care Staff or the Facility Admin file a report (a draft). */
export const fileIncidentReport = route<{ real: true; report: IncidentReport; told: GuardianTold[] }, void, FileIncidentBody>()(
  'POST', `${A}/guardian/incident-reports`, { honest: true });

export interface IncidentReportsResponse {
  real: true;
  scope: IncidentScope;
  count: number;
  reports: IncidentReport[];
}

/**
 * GET /guardian/incident-reports?status=: the charge nurse inbox (default the open drafts).
 * Care Staff who are not the charge nurse get the reports they filed. Aithora sends facility.
 */
export const incidentReports = route<IncidentReportsResponse, { status?: 'draft' | 'signed' | 'all'; facility?: string }>()(
  'GET', `${A}/guardian/incident-reports`, { honest: true });

export interface IncidentReportResponse {
  real: true;
  scope: IncidentScope;
  report: IncidentReport;
}

/** GET /guardian/incident-reports/:id: one report, with the same access as the inbox. */
export const incidentReport = route<IncidentReportResponse, { facility?: string }>()('GET', `${A}/guardian/incident-reports/:id`, { honest: true });

export interface IncidentActionResponse extends IncidentReportResponse {
  step: 'doctor_told' | 'family_told' | 'report_filed' | 'signed';
  at: string;
  ledgerRef: string | null;
  /** On sign: when the investigation is due. */
  investigationDueAt?: string | null;
}

/** Who a Tell the family or Tell the doctor step is about. */
export type IncidentTellWho = 'family' | 'doctor';

/** A family member linked to the resident in the app (the family circle, revoked links left out). */
export interface IncidentFamilyContact {
  userId: string;
  name: string | null;
  /** Their phone on record, for "I called them". */
  phone: string | null;
}

/** One of the resident's practitioners on record (practitioners.ts listResidentPractitioners). */
export interface IncidentPractitionerContact {
  practitionerId: string;
  name: string;
  credential: string | null;
  /** The practitioner's role for this resident (ccm, bhi). */
  role: string;
  /** No phone is kept for a practitioner yet: always null. */
  phone: string | null;
}

/** SBAR: what the nurse reads to the doctor on the call. */
export interface IncidentSbar {
  situation: string;
  background: string;
  assessment: string;
  request: string;
}

/**
 * The draft. `source` atlas: Atlas wrote it from the report (its label says to check it).
 * template: built from the report's facts because Atlas is off or stopped, or its draft did
 * not pass the check; `reason` says which. A template never claims to be Atlas.
 */
export interface IncidentTellDraft {
  source: 'atlas' | 'template';
  label: string;
  /** The whole draft as one text (for the doctor, the four SBAR parts on four lines). */
  text: string;
  /** The doctor's SBAR, in parts. Null for the family. */
  sbar: IncidentSbar | null;
  model: string | null;
  reason: string | null;
}

export interface IncidentTellResponse {
  real: true;
  scope: IncidentScope;
  who: IncidentTellWho;
  reportId: string;
  residentName: string | null;
  /** family: the linked family members. Empty when none is linked. */
  family: IncidentFamilyContact[];
  /** doctor: the resident's practitioners on record. Empty when none is on record. */
  practitioners: IncidentPractitionerContact[];
  /** Plain words when nobody is on record ("No family member is linked in the app..."), else null. */
  noneOnRecord: string | null;
  /** The ways this step may be recorded. The family with nobody linked: phone only. */
  methods: IncidentToldMethod[];
  draft: IncidentTellDraft;
}

/**
 * GET /guardian/incident-reports/:id/tell?who=family|doctor: who to tell and a draft to tell
 * them with. Writes nothing. Only the charge nurse on shift or the Facility Admin.
 */
export const incidentTell = route<IncidentTellResponse, { who: IncidentTellWho }>()(
  'GET', `${A}/guardian/incident-reports/:id/tell`, { honest: true });

/**
 * Tell the family. in_app: `message` is sent on the care team's family thread (the Family
 * Portal's own Send) and every linked family member goes on the 483.10(g)(14) record.
 * phone: the nurse called; `spokenWith` is who they spoke with (`spokenWithUserId` when that
 * person is a linked family member, who then goes on the record), `note` optional.
 */
export type IncidentFamilyToldBody =
  | { method: 'in_app'; message: string }
  | { method: 'phone'; spokenWith: string; spokenWithUserId?: string | null; note?: string | null };

/**
 * Tell the doctor: how (phone, fax, portal), who it went to (`spokenWith`, or the picked
 * `practitionerId`, whose name is used when `spokenWith` is empty), and what the doctor said
 * or ordered (`orders`, optional).
 */
export interface IncidentDoctorToldBody {
  method: DoctorToldMethod;
  practitionerId?: string | null;
  spokenWith?: string | null;
  orders?: string | null;
}

/** POST /guardian/incident-reports/:id/doctor-told: Tell the doctor (once, with how and who). */
export const incidentDoctorTold = route<IncidentActionResponse, void, IncidentDoctorToldBody>()(
  'POST', `${A}/guardian/incident-reports/:id/doctor-told`, { honest: true });

/** POST /guardian/incident-reports/:id/family-told: Tell the family (once, sent in the app or by phone). */
export const incidentFamilyTold = route<IncidentActionResponse & { messageId: string | null; notified: string[] }, void, IncidentFamilyToldBody>()(
  'POST', `${A}/guardian/incident-reports/:id/family-told`, { honest: true });

/** POST /guardian/incident-reports/:id/state-report-filed: "Mark state report filed". */
export const incidentStateReportFiled = route<IncidentActionResponse, void, { note?: string }>()(
  'POST', `${A}/guardian/incident-reports/:id/state-report-filed`, { honest: true });

/** POST /guardian/incident-reports/:id/sign: finalizes the report (409 with signBlockers until ready). */
export const incidentSign = route<IncidentActionResponse, void, { note?: string }>()(
  'POST', `${A}/guardian/incident-reports/:id/sign`, { honest: true });

export const incidentReportApi = {
  incidentTypes,
  fileIncidentReport,
  incidentReports,
  incidentReport,
  incidentTell,
  incidentDoctorTold,
  incidentFamilyTold,
  incidentStateReportFiled,
  incidentSign,
} as const;
